import { CheckCircle, Circle, XCircle } from "lucide-react";

import type { AssertionRenderModel } from "../../assertions";

interface AssertionRendererProps {
  model: AssertionRenderModel;
}

function StatusIcon({ value }: { value: boolean | null }) {
  if (value === true) {
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  }

  if (value === false) {
    return <XCircle className="h-4 w-4 text-red-600" />;
  }

  return <Circle className="h-4 w-4 text-muted-foreground" />;
}

function StatusBadge({ value }: { value: boolean | null }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <CheckCircle className="h-3.5 w-3.5" />
        Pass
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        <XCircle className="h-3.5 w-3.5" />
        Fail
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <Circle className="h-3.5 w-3.5" />
      Unknown
    </span>
  );
}

export function AssertionRenderer({ model }: AssertionRendererProps) {
  if (model.assertions.length === 0) {
    return (
      <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
        No assertions were provided for this column.
      </div>
    );
  }

  const passedCount = model.assertions.filter(assertion => assertion.value === true).length;
  const failedCount = model.assertions.filter(assertion => assertion.value === false).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded bg-muted px-2 py-0.5">{model.assertions.length} assertions</span>
        <span className="rounded bg-green-100 px-2 py-0.5 text-green-700">{passedCount} pass</span>
        <span className="rounded bg-red-100 px-2 py-0.5 text-red-700">{failedCount} fail</span>
      </div>

      <div className="space-y-2">
        {model.assertions.map(assertion => (
          <section key={assertion.key} className="rounded-lg border border-border/60 bg-card p-3">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2">
                <div className="mt-0.5">
                  <StatusIcon value={assertion.value} />
                </div>
                <p className="whitespace-pre-wrap break-words text-sm font-medium text-foreground">
                  {assertion.key || "Assertion"}
                </p>
              </div>
              <StatusBadge value={assertion.value} />
            </div>

            {assertion.reasoning ? (
              <div className="mt-2">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Reasoning
                </p>
                <p className="whitespace-pre-wrap break-words text-sm text-foreground/90">
                  {assertion.reasoning}
                </p>
              </div>
            ) : null}

            {assertion.citation ? (
              <div className="mt-2">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Citation
                </p>
                <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
                  {assertion.citation}
                </p>
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </div>
  );
}
